import { mat4, quat, vec3 } from 'gl-matrix';
import { Context } from '../context/Context';
import { RenderObject } from '../object/RenderObject';
import { RenderMode, RenderSettings } from './index';
import { Camera } from '../camera';
import { Texture } from '../texture';
import { ViewFrustum } from '../util';
import { createColorTexture } from './RenderTarget';

/**
 * Generates impostor textures by rendering a model from the side into an FBO.
 * The resulting texture captures the albedo output of the G-buffer shader,
 * producing a side-view image of the tree with correct textures and alpha.
 *
 * Height-based AO bake: the IMPOSTOR_AO shader path darkens fragments toward
 * the base of the mesh (high SSAO at runtime on grounded objects) and leaves
 * the top at full brightness (exposed, low runtime SSAO). Falloff is in world-
 * space metres so a 1m rock gets a gentle gradient while a 5m tree gets the
 * full effect.
 */
export class ImpostorTextureGenerator {
  /**
   * Render a single object from the side to produce an impostor texture.
   * The object is temporarily repositioned at the origin with scale=1.
   *
   * @param size   Resolution of the impostor texture (e.g. 256)
   * @returns A Texture containing the side-view impostor with alpha
   */
  public static generate(ctx: Context, obj: RenderObject, size = 256): Texture {
    const gl = ctx.gl;

    // Create FBO with a single RGBA color attachment + depth.
    // The G-buffer shader writes albedo to layout(location=0) — outputs
    // to locations 1/2 (normal, position) are silently discarded since
    // those framebuffer slots aren't attached.
    const colorTex = createColorTexture(gl, size, size, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE);
    const depthRb = gl.createRenderbuffer()!;
    gl.bindRenderbuffer(gl.RENDERBUFFER, depthRb);
    gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT16, size, size);

    const fb = gl.createFramebuffer()!;
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, colorTex, 0);
    gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, depthRb);
    gl.drawBuffers([gl.COLOR_ATTACHMENT0]);

    // Compute camera from object's unscaled local bounds.
    // We render with scale=1 so the camera matches the mesh-space extents.
    const bounds = obj.bounds!;
    const localMin = bounds.localMin;
    const localMax = bounds.localMax;
    const centerY = (localMin[1] + localMax[1]) / 2;
    const height = localMax[1] - localMin[1];
    const width = Math.max(localMax[0] - localMin[0], localMax[2] - localMin[2]);
    const halfExtent = (Math.max(width, height) / 2) * 1.1; // 10% margin

    // Orthographic camera from +Z looking at -Z
    const eye = vec3.fromValues(0, centerY, halfExtent * 3);
    const target = vec3.fromValues(0, centerY, 0);
    const up = vec3.fromValues(0, 1, 0);

    const viewMatrix = mat4.lookAt(mat4.create(), eye, target, up);
    // Flip Y in the projection so the texture stores top-down (matching
    // standard UV convention where V=0 is top).  The billboard mesh uses
    // V=0 at bottom / V=1 at top, and OpenGL FBOs store row 0 at the
    // bottom, so swapping bottom/top here makes everything consistent.
    const projMatrix = mat4.ortho(
      mat4.create(),
      -halfExtent,
      halfExtent,
      halfExtent, // flipped: top → bottom param
      -halfExtent, // flipped: bottom → top param
      0.1,
      halfExtent * 6,
    );

    const impostorCamera = new ImpostorCamera(viewMatrix, projMatrix, size);

    // Save object state and render at origin with identity transform
    const savedPos: [number, number, number] = [
      obj.trs.position[0],
      obj.trs.position[1],
      obj.trs.position[2],
    ];
    const savedRot = obj.trs.getRotationQuat();
    const savedScale = obj.trs.scale;
    obj.trs.position[0] = 0;
    obj.trs.position[1] = 0;
    obj.trs.position[2] = 0;
    obj.trs.setRotationQuat(quat.create());
    obj.trs.scale = 1;

    // Set GL state
    gl.viewport(0, 0, size, size);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    gl.enable(gl.DEPTH_TEST);
    gl.depthMask(true);
    gl.disable(gl.CULL_FACE);

    const settings: RenderSettings = {
      mode: RenderMode.GBuffer,
      lights: [],
      objects: [obj],
      impostorOutput: true,
      impostorHeightBounds: [localMin[1], localMax[1]],
    };
    obj.render(ctx, impostorCamera, settings);

    // Restore object state
    obj.trs.position[0] = savedPos[0];
    obj.trs.position[1] = savedPos[1];
    obj.trs.position[2] = savedPos[2];
    obj.trs.setRotationQuat(savedRot);
    obj.trs.scale = savedScale;

    // Restore GL state
    gl.enable(gl.CULL_FACE);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, ctx.viewPort.width, ctx.viewPort.height);

    // Clean up FBO resources (keep the color texture)
    gl.deleteFramebuffer(fb);
    gl.deleteRenderbuffer(depthRb);

    // Wrap in a Texture object
    const texture = new Texture();
    texture.glTexture = colorTex;
    texture.translucent = true;
    return texture;
  }
}

/** Minimal camera for impostor rendering. */
class ImpostorCamera extends Camera {
  private _view: mat4;
  private _proj: mat4;
  private _frustum: ViewFrustum;

  constructor(view: mat4, proj: mat4, size: number) {
    super({ width: size, height: size });
    this.name = 'ImpostorCamera';
    this._view = view;
    this._proj = proj;

    const vp = mat4.mul(mat4.create(), proj, view);
    const invVp = mat4.invert(mat4.create(), vp)!;
    this._frustum = new ViewFrustum(invVp);
  }

  get modelViewMatrix(): mat4 {
    return this._view;
  }
  get projectionMatrix(): mat4 {
    return this._proj;
  }
  get cameraPosition(): vec3 {
    return vec3.fromValues(0, 0, 100);
  }
  get targetPosition(): vec3 {
    return vec3.fromValues(0, 0, 0);
  }
  get frustum(): ViewFrustum {
    return this._frustum;
  }
  prepareFrame(): void {}
}
